/**
 * Pure utility functions for JSON import parsing.
 *
 * Extracted from JsonImportModal.tsx to reduce function complexity.
 */

import type { CriticalityLevel, ImportEndpointItem } from '../../../types/case-container'

interface RawJsonEndpoint {
  hostname?: string
  name?: string
  host?: string
  deviceType?: string
  device_type?: string
  type?: string
  nodeCategory?: string
  node_category?: string
  ipAddresses?: string[]
  ip_addresses?: string[]
  ip?: string
  domain?: string
  operatingSystem?: string
  operating_system?: string
  os?: string
  osVersion?: string
  os_version?: string
  roles?: string[]
  ouPath?: string
  ou_path?: string
  location?: string
  department?: string
  criticality?: string
  notes?: string
}

interface JsonTemplateFormat {
  templateInfo?: {
    batchId?: string
    generatedAt?: string
    templateVersion?: string
  }
  metadata?: Record<string, unknown>
  fieldDefinitions?: unknown[]
  validValues?: Record<string, unknown>
  data?: RawJsonEndpoint[]
  endpoints?: RawJsonEndpoint[]
}

export type { RawJsonEndpoint, JsonTemplateFormat }

export function extractRawEndpoints(
  parsed: RawJsonEndpoint[] | JsonTemplateFormat
): RawJsonEndpoint[] {
  if (Array.isArray(parsed)) return parsed
  if (parsed.data && Array.isArray(parsed.data)) return parsed.data
  if (parsed.endpoints && Array.isArray(parsed.endpoints)) return parsed.endpoints
  return []
}

function pickHostname(ep: RawJsonEndpoint): string | undefined {
  return ep.hostname ?? ep.name ?? ep.host
}

function pickDeviceType(ep: RawJsonEndpoint): string {
  return ep.deviceType ?? ep.device_type ?? ep.type ?? 'workstation'
}

function pickIpAddresses(ep: RawJsonEndpoint): string[] | undefined {
  return ep.ipAddresses ?? ep.ip_addresses ?? (ep.ip ? [ep.ip] : undefined)
}

function pickOperatingSystem(ep: RawJsonEndpoint): string | undefined {
  return ep.operatingSystem ?? ep.operating_system ?? ep.os
}

function attachOptionalStrings(item: ImportEndpointItem, ep: RawJsonEndpoint): void {
  if (ep.domain) item.domain = ep.domain
  if (ep.location) item.location = ep.location
  if (ep.department) item.department = ep.department
  if (ep.notes) item.notes = ep.notes
  const osVersion = ep.osVersion ?? ep.os_version
  if (osVersion) item.osVersion = osVersion
  const ouPath = ep.ouPath ?? ep.ou_path
  if (ouPath) item.ouPath = ouPath
}

export function mapRawToImportItem(ep: RawJsonEndpoint): ImportEndpointItem | null {
  const hostname = pickHostname(ep)
  if (!hostname) return null
  const item: ImportEndpointItem = { hostname, deviceType: pickDeviceType(ep) }
  const ipAddresses = pickIpAddresses(ep)
  if (ipAddresses) item.ipAddresses = ipAddresses
  const operatingSystem = pickOperatingSystem(ep)
  if (operatingSystem) item.operatingSystem = operatingSystem
  attachOptionalStrings(item, ep)
  if (ep.criticality) item.criticality = ep.criticality as CriticalityLevel
  if (ep.roles && ep.roles.length > 0) item.roles = ep.roles
  return item
}

export function mapAllEndpoints(rawEndpoints: RawJsonEndpoint[]): ImportEndpointItem[] {
  const result: ImportEndpointItem[] = []
  for (const ep of rawEndpoints) {
    const item = mapRawToImportItem(ep)
    if (item) result.push(item)
  }
  return result
}

const PREVIEW_LIMIT = 10

export interface PreviewParseSuccess {
  ok: true
  isTemplateFormat: boolean
  rawCount: number
  preview: ImportEndpointItem[]
}

export interface PreviewParseFailure {
  ok: false
  error: string
}

export type PreviewParseResult = PreviewParseSuccess | PreviewParseFailure

export function parseJsonPreview(text: string): PreviewParseResult {
  let parsed: RawJsonEndpoint[] | JsonTemplateFormat
  try {
    parsed = JSON.parse(text) as RawJsonEndpoint[] | JsonTemplateFormat
  } catch {
    return { ok: false, error: 'Invalid JSON format' }
  }
  const rawEndpoints = extractRawEndpoints(parsed)
  if (rawEndpoints.length === 0) {
    return {
      ok: false,
      error:
        'No endpoints found in JSON. Expected an array, or object with "data" or "endpoints" array.',
    }
  }
  const preview: ImportEndpointItem[] = []
  for (const ep of rawEndpoints.slice(0, PREVIEW_LIMIT)) {
    const item = mapRawToImportItem(ep)
    if (item) preview.push(item)
  }
  if (preview.length === 0) {
    return {
      ok: false,
      error: 'No valid endpoints found. Each endpoint must have a hostname, name, or host field.',
    }
  }
  const isTemplateFormat = !Array.isArray(parsed) && Boolean(parsed.data)
  return { ok: true, isTemplateFormat, rawCount: rawEndpoints.length, preview }
}
